import {
  BASES,
  puedeEntrar,
  tienePagina,
  type Accesos,
  type ClaveDeSubsistema,
} from '@vigilancia/runtime';

/**
 * Qué subsistemas hay, cómo se llaman y por dónde navega cada uno.
 *
 * **UNA SOLA VERDAD PARA LOS TRES.** Hasta el 5/09 cada aplicación llevaba su
 * barra dentro: Operación tenía ocho destinos en su propio archivo de datos, y
 * Administración y el portal no tenían ninguna. Tres barras separadas divergen
 * —la primera vez que alguien renombra un módulo lo renombra en una— y, sobre
 * todo, **ninguna de las tres podía nombrar a las otras dos**, que es
 * exactamente lo que hace falta para pasar de un subsistema a otro sin volver a
 * identificarse.
 *
 * **ES UN CATÁLOGO Y NO MARCADO, PORQUE EL ROL LLEGA ENCIMA.** Lo que se pinta
 * arriba —los destinos de la barra y las otras superficies a las que se puede
 * saltar— es lo que el rol de cada persona permite. Construirlo a partir de
 * datos hizo que, cuando el rol llegó el 29/09/2026, cambiara el filtro y no las
 * tres cabeceras: `destinosVisibles` y `subsistemasAlcanzables`, al final.
 *
 * 🔑 **LA RUTA DE UN DESTINO ES SU PÁGINA EN EL RBAC DE NATCLAR.** El RBAC
 * registra cada página por la misma ruta que ve el navegador —`/admin/programas`—,
 * así que no hay tabla de equivalencias: un destino con ruta se enseña a quien
 * tiene esa página, y la guarda de su pantalla pide esa misma página
 * (`paginaDe`). Las páginas las da de alta la carga de la API
 * (`db/objetos/0014`); un destino nuevo con ruta tiene que estar allí, o nadie
 * podrá abrirlo.
 *
 * LOS DESTINOS SALEN DEL TO BE, NO DE UNA LISTA DE VIRTUDES. Cada subsistema
 * cubre los flujos que el diagrama le asigna, y por eso «Audiometrías» no está
 * en ninguno: ejecutar el examen es integración del HIS y no responsabilidad de
 * este sistema.
 *
 * · **Operación** — `DF-VIG-003/005/007`. Puesto médico del site y médico
 *   ocupacional del cliente: fichas, controles, resultados y seguimiento de SU
 *   unidad.
 * · **Administración** — `DF-VIG-001/002/008/009`. Natclar sede: programas y
 *   reglas, plan anual, biblioteca, aprobaciones, capacitaciones e
 *   investigación de enfermedad profesional, sobre TODOS los clientes.
 * · **Portal** — `DF-VIG-004/006/009`. El trabajador: consentimiento,
 *   resultados, avisos, material de capacitación y reporte de sospecha.
 */

export interface Destino {
  /** Clave del catálogo de textos. Ningún texto se escribe aquí. */
  readonly clave: string;
  /**
   * Ruta dentro del subsistema, cuando el destino existe.
   *
   * Sin ella se pinta **deshabilitado** en vez de como enlace muerto: un
   * destino que se ve pulsable y no lleva a ningún sitio enseña a desconfiar de
   * la barra entera, y eso no se desaprende cuando el destino aparezca.
   */
  readonly ruta?: string;
  /**
   * 🆕 5/10/2026 · **De todo el que entra a la aplicación**: no tiene página en el
   * RBAC, y se enseña siempre. Lo es la página de las notificaciones —cada uno ve
   * las suyas—. Su ruta no se pide a `paginaDe`: no hay página que pedir.
   */
  readonly deTodos?: true;
}

export interface Subsistema {
  readonly clave: ClaveDeSubsistema;
  /** Prefijo dentro del sitio, con barra final. Sale del reparto, no de aquí. */
  readonly base: string;
  /** Clave del catálogo con el nombre que lee su usuario. */
  readonly nombre: string;
  /** Clave del catálogo con la línea que dice para qué sirve. */
  readonly detalle: string;
  readonly destinos: readonly Destino[];
}

/**
 * Los tres, en el orden en que ocurren de verdad: se administra el programa, se
 * opera en el site, y el trabajador ve lo suyo. Es el orden del TO BE y también
 * el del conmutador.
 */
const CATALOGO = {
  operacion: {
    clave: 'operacion',
    base: BASES.operacion,
    nombre: 'cascaron.sitio.operacion',
    detalle: 'cascaron.sitio.operacion.detalle',
    destinos: [
      { clave: 'cascaron.nav.operacion.trabajadores' },
      { clave: 'cascaron.nav.operacion.programas' },
      { clave: 'cascaron.nav.operacion.clusterizacion' },
      { clave: 'cascaron.nav.operacion.agenda' },
      { clave: 'cascaron.nav.operacion.seguimiento' },
      { clave: 'cascaron.nav.operacion.interconsultas' },
      /*
        INFORMES · el listado de informes, y su dirección desde el 30/09/2026.

        Hasta ese día la matriz de audiometría vivía en la raíz de Operación y
        este destino se encendía sin ruta: era la única pantalla. Con la
        reportería la raíz queda libre —será el panel del subsistema, como la de
        Administración— y «Informes» lleva a un LISTADO; la Matriz Audiometría
        Base es un informe de ese listado, en su propia dirección debajo de ésta.
        Lo decidió Alex el 29/09.
      */
      { clave: 'cascaron.nav.operacion.informes', ruta: `${BASES.operacion}informes` },
      /*
        NOTIFICACIONES · 🆕 5/10/2026, al revisar Alex la segunda vuelta: «agrega en
        el topbar la página de notificaciones en las tres apps, y quita el que
        consideres menos relevante que no estemos usando». Va donde estaba
        «Indicadores», que dormía sin pantalla: los indicadores son los informes y
        sus vistas gráficas, y ésos ya tienen destino. Es de todo el que entra
        (`deTodos`).
      */
      {
        clave: 'cascaron.nav.operacion.notificaciones',
        ruta: `${BASES.operacion}notificaciones`,
        deTodos: true,
      },
    ],
  },

  admin: {
    clave: 'admin',
    base: BASES.admin,
    nombre: 'cascaron.sitio.admin',
    detalle: 'cascaron.sitio.admin.detalle',
    destinos: [
      { clave: 'cascaron.nav.admin.clientes' },
      /*
        PROGRAMAS · el módulo real, y SIGUE SIN RUTA a propósito.

        Su dirección —`/admin/programas`— quedó libre el 15/09 al mudarse
        Revisión a la suya, y es donde vivirá: enseñará los programas de
        vigilancia que hay en la base del cliente y, cuando se pueda escribir,
        será donde se configure un programa PARA una empresa. ⚠️ **No crea
        programas**: el catálogo ya existe y lo que este módulo gobierna es el
        vínculo empresa × programa.

        ✅ **Y ese día llegó el 16/09**: la pantalla existe y lee la base del
        cliente, así que el destino estrena ruta. Hasta entonces se pintó
        dormido a propósito — adelantar la ruta es lo que se hizo hasta el 10/09,
        un destino activo enseñando otra cosa, y es lo que costó descubrir que
        la gestión de programas no existía.
      */
      { clave: 'cascaron.nav.admin.programas', ruta: `${BASES.admin}programas` },
      /*
        REVISIÓN es el meta-modelo: programas, campos calculados, reglas de
        clusterización y la línea de tiempo.

        ⚠️ **NO ES «PROGRAMAS», Y HASTA EL 10/09 VIVÍA AHÍ.** Se colgó de ese
        destino porque las reglas son las de esos programas, y con el tiempo se
        vio lo que eso escondía: «Programas» del TO BE todavía no existía —la
        gestión de verdad—, así que el destino se pintaba activo enseñando otra
        cosa. Se le dio destino propio y Programas quedó libre y deshabilitado.

        ⚠️ **Y HASTA EL 15/09 SU RUTA ERA `/admin/` A SECAS**, que es la raíz del
        subsistema. Eso dejaba a Programas sin ninguna dirección que estrenar el
        día que existiera: no hay dos raíces. Ahora Revisión vive bajo su propio
        prefijo, `/admin/revision`, y **la raíz se queda vacía a propósito**
        —será el panel de Administración—. Lo decidió Alex.
      */
      { clave: 'cascaron.nav.admin.revision', ruta: `${BASES.admin}revision` },
      { clave: 'cascaron.nav.admin.plan' },
      { clave: 'cascaron.nav.admin.biblioteca' },
      { clave: 'cascaron.nav.admin.aprobaciones' },
      { clave: 'cascaron.nav.admin.capacitaciones' },
      { clave: 'cascaron.nav.admin.enfermedad' },
      /*
        ACCESOS · 🆕 6/10/2026 (D4 de Alex): los roles «Vigilancia 3.0 - …», qué abren
        y quién los tiene, administrados desde aquí y escritos en el RBAC de Natclar.
        No es un flujo del TO BE sino la consola del sistema, y por eso va al final,
        delante de lo que es de todos. Su página, `/admin/accesos`, está en el `0014`
        de la API **sin roles**: hasta que alguien se la dé desde el HIS, nadie la ve.
      */
      { clave: 'cascaron.nav.admin.accesos', ruta: `${BASES.admin}accesos` },
      /* NOTIFICACIONES · 🆕 5/10/2026, como en Operación: donde estaba «Indicadores»,
         que dormía sin pantalla —los indicadores son informes, y los informes viven
         en Operación—. */
      {
        clave: 'cascaron.nav.admin.notificaciones',
        ruta: `${BASES.admin}notificaciones`,
        deTodos: true,
      },
    ],
  },

  /*
    CINCO Y NO OCHO, y no es que falten. Su gente llega por el enlace de un
    mensaje, en un teléfono de gama baja y con la red de una mina: cada destino
    de más es una decisión de más delante de alguien que entró a hacer UNA cosa.
    Lo que hay es lo que el TO BE le asigna, y nada más.
  */
  portal: {
    clave: 'portal',
    base: BASES.portal,
    nombre: 'cascaron.sitio.portal',
    detalle: 'cascaron.sitio.portal.detalle',
    destinos: [
      { clave: 'cascaron.nav.portal.consentimientos' },
      { clave: 'cascaron.nav.portal.resultados' },
      /* NOTIFICACIONES · 🆕 5/10/2026, como en Operación: donde estaba «Avisos», que
         eran esto mismo —lo que el sistema le cuenta al trabajador—, y en su sitio. */
      {
        clave: 'cascaron.nav.portal.notificaciones',
        ruta: `${BASES.portal}notificaciones`,
        deTodos: true,
      },
      { clave: 'cascaron.nav.portal.capacitaciones' },
      { clave: 'cascaron.nav.portal.reportar' },
    ],
  },
} as const satisfies Readonly<Record<ClaveDeSubsistema, Subsistema>>;

export const SUBSISTEMAS: typeof CATALOGO = Object.freeze(CATALOGO);

/**
 * Las claves de destino que existen, como unión de literales.
 *
 * Sale del propio catálogo: quien diga en qué destino está no puede escribir
 * una clave que no exista. **Sin esto el error sería silencioso** —una clave mal
 * escrita no marca ningún destino y la barra se pinta entera sin nada activo—,
 * que es justo la clase de fallo que no da la cara hasta que alguien la mira.
 */
export type ClaveDeDestino = (typeof CATALOGO)[ClaveDeSubsistema]['destinos'][number]['clave'];

/**
 * Los destinos de un subsistema, con el tipo ANCHO.
 *
 * El catálogo se declara `as const` para que `ClaveDeDestino` sea una unión de
 * literales y una clave mal escrita no compile. El precio es que sus objetos no
 * declaran `ruta` cuando no la tienen, así que recorrerlos genéricamente pelea
 * con el compilador. Este accesor paga ese precio una sola vez, aquí.
 */
export function destinosDe(clave: ClaveDeSubsistema): readonly Destino[] {
  return SUBSISTEMAS[clave].destinos;
}

/**
 * Las claves de los destinos que TIENEN pantalla, que son los que tienen página
 * en el RBAC. Sale del catálogo, como `ClaveDeDestino`: pedir la página de un
 * destino que todavía no existe no compila.
 */
type DestinoConRuta = Exclude<
  Extract<(typeof CATALOGO)[ClaveDeSubsistema]['destinos'][number], { readonly ruta: string }>,
  { readonly deTodos: true }
>;
export type ClaveDeDestinoConPantalla = DestinoConRuta['clave'];

/**
 * La página del RBAC que abre un destino: su propia ruta.
 *
 * La usan las guardas de las pantallas, para que la barra y la guarda pidan
 * exactamente la misma cadena. Dos copias de `/admin/programas` —una aquí y otra
 * en la ruta— se separarían el día que una cambiara, y el síntoma sería una
 * pantalla que se enseña en la barra y se niega al abrirla.
 */
export function paginaDe(clave: ClaveDeDestinoConPantalla): string {
  for (const subsistema of Object.values(SUBSISTEMAS)) {
    for (const destino of subsistema.destinos as readonly Destino[]) {
      if (destino.clave === clave && destino.ruta !== undefined) return destino.ruta;
    }
  }
  // El tipo lo impide; esto es para quien lo salte con un molde.
  throw new Error(`El destino ${clave} no tiene pantalla.`);
}

/**
 * Los destinos de la barra que ve ESTA persona · 🆕 29/09/2026.
 *
 * · **Sin la aplicación, ninguno.** La barra de una aplicación que no se puede
 *   abrir no es un mapa: es una promesa que la pantalla no va a cumplir.
 * · **Con la aplicación, los que no tienen pantalla todavía, siempre**, como
 *   hasta ahora —dormidos, y son el mapa del producto: no abren nada—.
 * · **Y los que tienen pantalla, sólo si se tiene su página.** Uno que se
 *   enseña y se niega al pulsarlo enseña a desconfiar de la barra entera.
 * · 🆕 5/10/2026 · **Los de todo el que entra, siempre**: no tienen página que
 *   pedir (`deTodos`).
 *
 * SIN ACCESOS —sin nadie que haya entrado— se pinta el catálogo entero, que es
 * lo que el armazón hacía antes del rol. En las tres aplicaciones no pasa: no
 * se monta nada sin sesión, y con sesión siempre hay accesos.
 */
export function destinosVisibles(
  clave: ClaveDeSubsistema,
  accesos: Accesos | undefined,
): readonly Destino[] {
  const destinos = destinosDe(clave);
  if (accesos === undefined) return destinos;
  if (!puedeEntrar(accesos, clave)) return [];
  return destinos.filter(
    (destino) =>
      destino.ruta === undefined || destino.deTodos === true || tienePagina(accesos, destino.ruta),
  );
}

/** El orden del conmutador, fijo y no el de las claves de un objeto. */
const ORDEN: readonly ClaveDeSubsistema[] = ['operacion', 'admin', 'portal'];

/**
 * A qué subsistemas puede ir quien está mirando la pantalla, aparte del suyo.
 *
 * **AQUÍ ENTRÓ EL ROL, EL 29/09/2026, Y FUE LO ÚNICO QUE CAMBIÓ**, como se dejó
 * escrito el 5/09: ni las tres cabeceras, ni el menú, ni el catálogo. Hasta ese
 * día devolvía todos los que no eran el actual —lo que cualquiera con credencial
 * podía hacer escribiendo la ruta—; desde entonces, **sólo aquéllos cuyo módulo
 * le abre el RBAC de Natclar**.
 *
 * SIN SESIÓN NO DEVUELVE NADA, y no es cosmética: sin identidad no hay a dónde
 * ir —el otro subsistema rebotaría a la puerta— y ofrecer el salto sería
 * prometer algo que no se puede cumplir.
 */
export function subsistemasAlcanzables(
  accesos: Accesos | undefined,
  desde: ClaveDeSubsistema,
): readonly Subsistema[] {
  if (accesos === undefined) return [];
  return ORDEN.filter((clave) => clave !== desde && puedeEntrar(accesos, clave)).map(
    (clave) => SUBSISTEMAS[clave],
  );
}
